import { ChevronDown } from 'lucide-react';
import React from 'react';

import { COLORS } from './constants';
import { Tip } from './Tip';

// The compact issue-row pattern originally built for ConditionScoreView's
// inspection findings (urgency rail, colored chip, hover state, chevron,
// inline expansion). Extracted here so every module that lists severity-
// ranked findings — condition issues today, HOA environmental risks next —
// renders them identically instead of re-implementing the row by eye.
export type IssueRowUrgency = 'Critical' | 'Urgent' | 'Moderate' | 'Low' | 'Monitor';

export interface IssueRowProps {
  testId?: string;
  bodyTestId?: string;
  title: string;
  /** Optional secondary line under the title (cost, category, etc.). Rendered
   *  whenever defined, even as an empty string, to match the original row's
   *  reserved second line. Accepts a node (not just a string) so callers can
   *  compose extra inline content next to the meta text — e.g. HOA
   *  environmental risks appending Owner/HOA actor badges. */
  meta?: React.ReactNode;
  urgency: IssueRowUrgency | string;
  /** Optional explicit chip/rail color for callers whose display taxonomy is
   *  more specific than the raw urgency tier. */
  urgencyColor?: string;
  /** Overrides the chip's displayed text without affecting color — `urgency`
   *  still drives the color lookup. For callers whose visible label needs
   *  to say something more specific than the severity tier itself allows
   *  (e.g. HOA environmental risks, where "Critical" alone doesn't
   *  distinguish a legal restriction from an active equipment failure). */
  urgencyLabel?: string;
  urgencyTooltip?: string;
  /** Defaults to whether children are present. Set explicitly when a row should stay clickable with no body content. */
  expandable?: boolean;
  expanded: boolean;
  onToggle?: () => void;
  isMobile?: boolean;
  /** Moves the urgency chip + expand caret out of the title row and down
   *  next to `meta`, bottom-right, letting the title use the row's full
   *  width. Off by default so existing callers (ConditionScoreView, which
   *  already passes `isMobile`) keep the inline layout; HOADetailView's
   *  EnvironmentalRiskModule opts in on mobile only. */
  stackedUrgency?: boolean;
  children?: React.ReactNode;
}

export const IssueRow = ({
  testId,
  bodyTestId,
  title,
  meta,
  urgency,
  urgencyColor,
  urgencyLabel,
  urgencyTooltip,
  expandable,
  expanded,
  onToggle,
  isMobile = false,
  stackedUrgency = false,
  children,
}: IssueRowProps) => {
  const urgencyKey = urgency.toLowerCase() as keyof typeof COLORS;
  const urgColor = urgencyColor ?? COLORS[urgencyKey] ?? COLORS.monitor;
  const hasBody = Boolean(children);
  const canExpand = expandable ?? hasBody;

  const handleToggle = (e: React.MouseEvent) => {
    if (!canExpand || !onToggle) return;
    e.stopPropagation();
    onToggle();
  };

  const urgencyChipAndCaret = (
    <>
      <Tip text={urgencyTooltip} style={{ display: 'flex', alignItems: 'center' }}>
        {/* urgency said once, in the legend's language: its hairline and its name, no stripe and no chip */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--tx-muted)',
            flexShrink: 0,
            cursor: urgencyTooltip ? 'help' : 'default',
            fontFamily: "'Manrope', sans-serif",
          }}>
          <span style={{ width: 2, height: 12, borderRadius: 1, background: urgColor }} />
          {urgencyLabel ?? urgency}
        </span>
      </Tip>
      {canExpand && (
        <span
          style={{
            color: COLORS.textDim,
            marginLeft: 4,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}>
          <ChevronDown size={14} />
        </span>
      )}
    </>
  );

  return (
    // a flat row parted from the next by a hairline (Tradehaus rows), not a tinted tile
    <div style={{ borderTop: '1px solid var(--tx-line)' }}>
      <div
        data-testid={testId}
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: stackedUrgency ? 'flex-start' : 'center',
          padding: isMobile ? '10px 0' : '11px 0',
          gap: 10,
          cursor: canExpand ? 'pointer' : 'default',
        }}>
        {stackedUrgency ? (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-text)', textWrap: 'balance' }}>
              {title}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 10,
                marginTop: 2,
              }}>
              <span style={{ fontSize: 11, color: COLORS.textDim, minWidth: 0 }}>{meta}</span>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
                {urgencyChipAndCaret}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', paddingRight: 24 }}>
              <div
                style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-text)', textWrap: 'balance' }}>
                {title}
              </div>
              {meta !== undefined && (
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 3 }}>{meta}</div>
              )}
            </div>
            {urgencyChipAndCaret}
          </>
        )}
      </div>
      {expanded && hasBody && (
        <div
          data-testid={bodyTestId}
          style={{
            padding: '0 0 14px',
            fontSize: 13,
            color: 'var(--tx-muted)',
            lineHeight: 1.5,
          }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default IssueRow;
